import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatModels from "@medsurf/flat-models";
import * as FlatFacades from '../../internal';

@Injectable()
export class AnnotationEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static copyAnnotation(store: Store,
                        mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                        id: string,
                        page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                        image: FlatModels.ImageEntityModels.Image | null,
                        currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom | null = null,
                        clusterParentUpdate = false) {
    // Check If Annotation Mapping Exits
    if (mappings.annotation[id]) {
      return of(undefined);
    }
    
    // Get Data
    const typedAnnotations = store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotation
    const annotation = structuredClone(typedAnnotations[id]);
    
    // Update Annotation Mapping
    mappings.annotation[id] = uuid();
    
    // Update Annotation
    annotation.id = mappings.annotation[id];
    annotation.slide = page.id;
    annotation.images = [];
    
    return of(annotation.id).pipe(
      mergeMap(() => {
        // Copy Annotation By Type
        switch(annotation.type) {
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker:
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Text:
            return AnnotationEntityFacade.copyMarker(store, page, image, annotation as FlatModels.AnnotationEntityModels.Marker, currentPositionAndZoom, clusterParentUpdate);
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm:
            return AnnotationEntityFacade.copyFreeForm(store, page, image, annotation as FlatModels.AnnotationEntityModels.FreeForm, currentPositionAndZoom, clusterParentUpdate);
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap:
            return AnnotationEntityFacade.copyKeymap(store, mappings, page, image, annotation as FlatModels.AnnotationEntityModels.Keymap, currentPositionAndZoom, clusterParentUpdate);
          default:
            throw new FlatModels.ErrorControlModels.MedsurfError(
              'notification.type_not_supported',
              FlatModels.ErrorControlModels.ErrorLevel.enum.error,
              FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
              "LIBS.FlatFacades.AnnotationEntityFacade.copyAnnotation"
            );
        }
      }),
      mergeMap(() => of(annotation.id))
    )
  }
  
  static copyAnnotations(store: Store,
                         mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                         ids: string[],
                         page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                         image: FlatModels.ImageEntityModels.Image | null,
                         currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom | null = null,
                         clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Copy Annotations
    return forkJoin(ids.map(id => AnnotationEntityFacade.copyAnnotation(store, mappings, id, page, image, currentPositionAndZoom, true))).pipe(
      mergeMap((newIds) => {
        const selectIds = newIds.reduce((arr, id) => {
          if (id !== undefined) {
            arr.push(id);
          }
          return arr;
        }, [] as string[]);
        return of(selectIds);
      }),
      mergeMap((selectIds) => {
        if (clusterParentUpdate || !image) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
      mergeMap((selectIds) => {
        if (clusterParentUpdate) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
    );
  }
  
  static removeAnnotation(store: Store,
                          id: string,
                          clusterParentUpdate = false) {
    // Get Data
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const typedAnnotations = store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotation
    const annotation = structuredClone(typedAnnotations[id]) as FlatModels.AnnotationEntityModels.AnnotationEntityType;
    if (!annotation) {
      return of(undefined);
    }
    
    // Get Page
    const page = structuredClone(typedPages[annotation.slide]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    
    // Create Marker Pipe Object
    let annotationPipeModel: FlatModels.PipeEntityModels.AnnotationPipeObject;
    switch (annotation.type) {
      case 'Marker':
        annotationPipeModel = {
          type: annotation.type,
          data: {},
          annotation: annotation,
        };
        break;
      case 'Text':
        annotationPipeModel = {
          type: annotation.type,
          data: {},
          annotation: annotation,
        };
        break;
      case 'FreeForm':
        annotationPipeModel = {
          type: annotation.type,
          data: {},
          annotation: annotation,
        };
        break;
      case 'Keymap':
        annotationPipeModel = {
          type: annotation.type,
          data: {},
          annotation: annotation,
        };
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          "notification.not_implemented",
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.AnnotationEntityFacade.removeAnnotation",
        );
    }
    
    // Remove Marker
    return of(annotationPipeModel).pipe(
      mergeMap((result) =>
        store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveAnnotation(id)).pipe(
          mergeMap((data) => of(Object.assign(result, { data: data }))),
        )
      ),
      mergeMap((result) => {
        let marker: FlatModels.AnnotationEntityModels.Marker;
        let freeform: FlatModels.AnnotationEntityModels.FreeForm;
        let keymap: FlatModels.AnnotationEntityModels.Keymap;
        switch (annotation.type) {
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker:
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Text:
            marker = annotation as FlatModels.AnnotationEntityModels.Marker;
            return AnnotationEntityFacade.removeMarker(store, marker).pipe(
              mergeMap((data) => of(Object.assign(result, { data: data }))),
            )
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm:
            freeform = annotation as FlatModels.AnnotationEntityModels.FreeForm;
            return AnnotationEntityFacade.removeFreeForm(store, freeform).pipe(
              mergeMap((data) => of(Object.assign(result, { data: data }))),
            )
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap:
            keymap = annotation as FlatModels.AnnotationEntityModels.Keymap;
            return AnnotationEntityFacade.removeKeymap(store, keymap).pipe(
              mergeMap((data) => of(Object.assign(result, { data: data }))),
            )
          default:
            throw new FlatModels.ErrorControlModels.MedsurfError(
              'notification.type_not_supported',
              FlatModels.ErrorControlModels.ErrorLevel.enum.error,
              FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
              "LIBS.FlatFacades.AnnotationEntityFacade.removeAnnotation"
            );
        }
      }),
      mergeMap((result) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        if ((annotation.images || []).length > 0) {
          return forkJoin((annotation.images || []).map(image => FlatFacades.ImageEntityFacade.removeAnnotationFromImage(store, annotation.id, image))).pipe(
            mergeMap((data) => of(Object.assign(result, { data: data })))
          );
        }
        return of(result);
      }),
      mergeMap((result) => {
        if ((annotation.groups || []).length > 0) {
          return forkJoin((annotation.groups || []).map(group => FlatFacades.GroupEntityFacade.removeAnnotationFromGroup(store, annotation.id, group))).pipe(
            mergeMap((data) => of(Object.assign(result, { data: data })))
          );
        }
        return of(result);
      }),
      mergeMap((result) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        // Get Pois
        const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
        const poiAnnotations = page.pois.reduce((arr, poi) => {
          const entity = structuredClone(typedPois[poi]);
          if (entity) {
            const annotationToPointOfInterest = entity.annotationToPointOfInterest.find(atpoi => atpoi.annotation === annotation.id);
            if (annotationToPointOfInterest) {
              arr.push({
                annotation: annotation.id,
                poi: poi
              });
            }
          }
          return arr;
        }, [] as { poi: string, annotation: string }[]);
        if (poiAnnotations.length > 0) {
          return forkJoin(poiAnnotations.map(poiAnnotation => FlatFacades.PoiEntityFacade.removeAnnotationFromPoi(store, poiAnnotation.annotation, poiAnnotation.poi))).pipe(
            mergeMap((data) => of(Object.assign(result, { data: data })))
          );
        }
        return of(result);
      }),
      mergeMap((result) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        if (annotation.slide) {
          return FlatFacades.PageEntityFacade.removeAnnotationFromPage(store, annotation.id, annotation.slide).pipe(
            mergeMap((data) => of(Object.assign(result, { data: data })))
          );
        }
        return of(result);
      }),
    );
  }
  
  static removeAnnotations(store: Store,
                           ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Remove Annotations
    return forkJoin(ids.map(id => AnnotationEntityFacade.removeAnnotation(store, id, true))).pipe(
      mergeMap((annotations: (FlatModels.PipeEntityModels.AnnotationPipeObject | undefined)[]) => {
        const filteredAnnotations = annotations.reduce((arr, annotation) => {
          if (annotation !== undefined) {
            arr.push(annotation);
          }
          return arr;
        }, [] as FlatModels.PipeEntityModels.AnnotationPipeObject[]);
        const typedAnnotationImages = filteredAnnotations.reduce((arr, annotationPipeObject) => {
          for (const image of annotationPipeObject.annotation.images || []) {
            const entity = arr[image];
            if (entity) {
              arr[image].annotations.push(annotationPipeObject.annotation.id);
            } else {
              arr[image] = {image, annotations: [annotationPipeObject.annotation.id]};
            }
          }
          return arr;
        }, {} as {[id: string]: {image: string, annotations: string[]}});
        const annotationImages = Object.values(typedAnnotationImages);
        if (annotationImages.length > 0) {
          return forkJoin(annotationImages.map(annotationImage => FlatFacades.ImageEntityFacade.removeAnnotationsFromImage(store, annotationImage.annotations, annotationImage.image))).pipe(
            mergeMap(() => of(filteredAnnotations)),
          );
        }
        return of(filteredAnnotations);
      }),
      mergeMap((annotations: FlatModels.PipeEntityModels.AnnotationPipeObject[] ) => {
        const typedAnnotationGroups = annotations.reduce((arr, annotationPipeObject) => {
          for (const group of annotationPipeObject.annotation.groups || []) {
            const entity = arr[group];
            if (entity) {
              arr[group].annotations.push(annotationPipeObject.annotation.id);
            } else {
              arr[group] = { group, annotations: [annotationPipeObject.annotation.id] };
            }
          }
          return arr;
        }, {} as {[id: string]: {group: string, annotations: string[]}});
        const annotationGroups = Object.values(typedAnnotationGroups);
        if (annotationGroups.length > 0) {
          return forkJoin(annotationGroups.map(annotationGroup => FlatFacades.GroupEntityFacade.removeAnnotationsFromGroup(store, annotationGroup.annotations, annotationGroup.group))).pipe(
            mergeMap(() => {
              return of(annotations);
            }),
          );
        }
        return of(annotations);
      }),
      mergeMap((annotations: FlatModels.PipeEntityModels.AnnotationPipeObject[] ) => {
        const typedAnnotationPois = annotations.reduce((arr, annotationPipeObject) => {
          const typedSlides = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
          const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
          // Get Pois
          const slide = structuredClone(typedSlides[annotationPipeObject.annotation.slide]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
          for (const poiId of slide.pois) {
            const poi = structuredClone(typedPois[poiId]);
            const annotationToPointOfInterest = poi.annotationToPointOfInterest.find(atpoi => atpoi.annotation === annotationPipeObject.annotation.id);
            if (annotationToPointOfInterest) {
              const entity = arr[poiId];
              if (entity) {
                arr[poiId].annotations.push(annotationPipeObject.annotation.id);
              } else {
                arr[poiId] = { poi: poiId, annotations: [annotationPipeObject.annotation.id] };
              }
            }
          }
          return arr;
        }, {} as {[id: string]: {poi: string, annotations: string[]}});
        const annotationPois = Object.values(typedAnnotationPois);
        if (annotationPois.length > 0) {
          return forkJoin(annotationPois.map(annotationPoi => FlatFacades.PoiEntityFacade.removeAnnotationsFromPoi(store, annotationPoi.annotations, annotationPoi.poi))).pipe(
            mergeMap(() => {
              return of(annotations);
            }),
          );
        }
        return of(annotations);
      }),
      mergeMap((annotations: FlatModels.PipeEntityModels.AnnotationPipeObject[]) => {
        const typedAnnotationPages = annotations.reduce((arr, annotationPipeObject) => {
          const page = annotationPipeObject.annotation.slide;
          const entity = arr[page];
          if (entity) {
            arr[page].annotations.push(annotationPipeObject.annotation.id);
          } else {
            arr[page] = {page, annotations: [annotationPipeObject.annotation.id]};
          }
          return arr;
        }, {} as {[id: string]: {page: string, annotations: string[]}});
        const annotationPages = Object.values(typedAnnotationPages);
        if (annotationPages.length > 0) {
          return forkJoin(annotationPages.map(annotationPage => FlatFacades.PageEntityFacade.removeAnnotationsFromPage(store, annotationPage.annotations, annotationPage.page))).pipe(
            mergeMap(() => {
              return of(annotations);
            }),
          );
        }
        return of(annotations);
      }),
    );
  }
  
  //<editor-fold desc="Marker">
  
  static addMarker(store: Store,
                   id: string,
                   page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                   image: FlatModels.ImageEntityModels.Image | null,
                   poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                   tipType: FlatModels.AnnotationEntityModels.TipType,
                   currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                   text: string | null,
                   isText = false,
                   clusterParentUpdate = false) {
    // Create New Marker
    const newMarker: FlatModels.AnnotationEntityModels.Marker = {
      id,
      type: FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker,
      fork: {
        x: null,
        y: null
      },
      selfTest: {
        ignore: false
      },
      images: image ? [image.id] : [],
      label: uuid(),
      source: uuid(),
      targets: (!isText ? [uuid()] : []),
      slide: page.id,
    };
    
    // Calculate Size And Position
    const scale = currentPositionAndZoom.isVM ? currentPositionAndZoom.currentPosition.zoom : 1;
    const normScale = currentPositionAndZoom.currentPosition.width / 800;
    const aspectRatio = currentPositionAndZoom.currentPosition.width / currentPositionAndZoom.currentPosition.height;
    const viewportWidth = 1 / currentPositionAndZoom.currentPosition.zoom;
    const freeFormWidth = viewportWidth * 0.1;
    const freeFormHeight = freeFormWidth * aspectRatio;
    const prevFontSize = 16 * normScale;
    const fontSize = Math.ceil(prevFontSize / scale);
    const sourcePosition = {
      x: currentPositionAndZoom.currentPosition.x + freeFormWidth / 2,
      y: currentPositionAndZoom.currentPosition.y - freeFormHeight / 2
    };
    
    // Update Image
    if (image) {
      image.annotations.push(newMarker.id);
    }
    
    // Update Poi
    if (poi) {
      // Add to Poi
      poi.annotationToPointOfInterest.push({
        id: uuid(),
        annotation: newMarker.id,
        pointOfInterest: poi.id,
        order: poi.annotationToPointOfInterest?.length || 0
      });
    }
    
    // Update Page
    page.annotations.push(newMarker.id);
    
    // Create Marker Pipe Object
    const markerPipeModel: FlatModels.PipeEntityModels.MarkerPipeObject = {
      type: newMarker.type,
      data: {},
      annotation: newMarker,
    };
    
    // Add Marker
    let layer: FlatModels.LayerEntityModels.Layer;
    return of(markerPipeModel).pipe(
      mergeMap((result: FlatModels.PipeEntityModels.MarkerPipeObject) =>
        store.dispatch(new FlatActions.AnnotationEntityActions.AddEntity(newMarker)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.MarkerPipeObject) =>
        FlatFacades.AnnotationLabelEntityFacade.addAnnotationLabel(store, newMarker.label, newMarker, text || 'Neuer Marker', currentPositionAndZoom.isVM ? fontSize : 12).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.MarkerPipeObject) =>
        FlatFacades.AnnotationSourceEntityFacade.addAnnotationSource(store, newMarker.source, newMarker, sourcePosition, currentPositionAndZoom.isVM ? currentPositionAndZoom.currentPosition.zoom : 1).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.MarkerPipeObject) => {
        if (!isText) {
          const offset = currentPositionAndZoom.currentPosition.zoom ? { x: -0.025 / currentPositionAndZoom.currentPosition.zoom, y: 0.15 / currentPositionAndZoom.currentPosition.zoom } : { x: -0.025, y: 0.15 };
          const targetPosition = {
            x: sourcePosition.x + offset.x,
            y: sourcePosition.y + offset.y,
          }
          return FlatFacades.AnnotationTargetEntityFacade.addAnnotationTarget(store, newMarker.targets[0], newMarker, tipType, targetPosition).pipe(
            mergeMap((data) => {
              result = Object.assign(result, {data: data.data});
              if (result.targets) {
                result.targets.push(data.annotationTarget);
              } else {
                result.targets = [data.annotationTarget];
              }
              return of(result);
            }),
          );
        }
        return of(result);
      }),
      mergeMap((result: FlatModels.PipeEntityModels.MarkerPipeObject) => {
        if (clusterParentUpdate || !image) {
          return of(result);
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((result: FlatModels.PipeEntityModels.MarkerPipeObject) => {
        if (!poi) {
          return of(result);
        }
        return store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((result: FlatModels.PipeEntityModels.MarkerPipeObject) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((data) => {
        if (image) {
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded(page.id));
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedLayerExpanded(image.layer));
          
          const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
          layer = typedLayers[image.layer];
          if (layer) {
            store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layer.position));
            
            const index = layer.images.indexOf(image.id);
            if (index > -1) {
              store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(index));
            }
          }
        } else {
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideAnnotationsExpanded(page.id));
        }
        store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(newMarker.id));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation));
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newMarker.id));
        return of(data);
      }),
    );
  }
  
  static updateMarkerPosition(store: Store,
                              annotation: FlatModels.AnnotationEntityModels.Marker | FlatModels.AnnotationEntityModels.Text,
                              currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Get Data
    const typedAnnotationSources = store.selectSnapshot(FlatStates.AnnotationSourceEntityState.typedEntities$);
    const typedAnnotationTargets = store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    let annotationSource = null;
    if (annotation.source) {
      annotationSource = structuredClone(typedAnnotationSources[annotation.source]);
    }
    let annotationTargets = annotation.targets.reduce((arr, annotationTarget) => {
      const entity = structuredClone(typedAnnotationTargets[annotationTarget]);
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.AnnotationTargetEntityModels.AnnotationTarget[]);
    
    // Update Marker Position
    const markerPositionHelper = new MarkerPositionHelper(annotation, annotationSource, annotationTargets, currentPositionAndZoom);
    annotation = markerPositionHelper.annotation;
    annotationSource = markerPositionHelper.annotationSource as FlatModels.AnnotationSourceEntityModels.AnnotationSource;
    annotationTargets = markerPositionHelper.annotationTargets;
    
    // Update Marker Position
    return store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(annotationSource)).pipe(
      mergeMap((data) => {
        if (annotationTargets.length > 0) {
          return forkJoin(annotationTargets.map(annotationTarget => store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(annotationTarget))));
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (annotation.fork) {
          return store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation));
        }
        return of(data);
      }),
      mergeMap((data) => {
        store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(annotation.id));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation));
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(annotation.id));
        return of(data);
      }),
    );
  }
  
  static copyMarker(store: Store,
                    page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                    image: FlatModels.ImageEntityModels.Image | null,
                    newMarker: FlatModels.AnnotationEntityModels.Marker,
                    currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom | null = null,
                    clusterParentUpdate = false) {
    // Update Page
    page.annotations.push(newMarker.id);
    
    // Update Image
    if (image) {
      if (newMarker.images) {
        newMarker.images.push(image.id);
      } else {
        newMarker.images = [image.id];
      }
      image.annotations.push(newMarker.id);
    }
    
    // Get Marker Label
    const newMarkerLabel = FlatFacades.AnnotationLabelEntityFacade.getCopyInstance(store, newMarker.label, newMarker);
    newMarker.label = newMarkerLabel.id;
    
    // Get Marker Source
    let newMarkerSource = FlatFacades.AnnotationSourceEntityFacade.getCopyInstance(store, newMarker.source, newMarker);
    newMarker.source = newMarkerSource.id;
    
    // Target Fix
    const targets = structuredClone(newMarker.targets);
    newMarker.targets = [];
    let updateMarker = structuredClone(newMarker) as FlatModels.AnnotationEntityModels.Marker | FlatModels.AnnotationEntityModels.Text;
    
    // Get Marker Targets
    let newMarkerTargets: FlatModels.AnnotationTargetEntityModels.AnnotationTarget[] = [];
    for (const target of targets) {
      const newMarkerTarget = FlatFacades.AnnotationTargetEntityFacade.getCopyInstance(store, target, newMarker);
      newMarkerTargets.push(newMarkerTarget);
      updateMarker.targets.push(newMarkerTarget.id);
    }
    
    // Update Marker Position
    if (currentPositionAndZoom) {
      const markerPositionHelper = new MarkerPositionHelper(updateMarker, newMarkerSource, newMarkerTargets, currentPositionAndZoom);
      updateMarker = markerPositionHelper.annotation;
      newMarkerSource = markerPositionHelper.annotationSource as FlatModels.AnnotationSourceEntityModels.AnnotationSource;
      newMarkerTargets = markerPositionHelper.annotationTargets;
    }
    
    // Copy Marker
    return store.dispatch(new FlatActions.AnnotationEntityActions.AddEntity(newMarker)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationLabelEntityActions.AddEntity(newMarkerLabel))
      ),
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationSourceEntityActions.AddEntity(newMarkerSource))
      ),
      mergeMap((data) => {
        if (newMarkerTargets.length > 0) {
          return forkJoin(newMarkerTargets.map(newMarkerTarget => store.dispatch(new FlatActions.AnnotationTargetEntityActions.AddEntity(newMarkerTarget))));
        }
        return of(data);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(updateMarker))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate || !image) {
          return of(data);
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image));
      }),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }
  
  static removeMarker(store: Store,
                      marker: FlatModels.AnnotationEntityModels.Marker) {
    // Create Marker Pipe Object
    const markerPipeModel: FlatModels.PipeEntityModels.MarkerPipeObject = {
      type: marker.type,
      data: {},
      annotation: marker,
    };
    
    return of(markerPipeModel).pipe(
      mergeMap((result) => {
        if (marker) {
          return store.dispatch(new FlatActions.AnnotationEntityActions.RemoveEntity(marker));
        }
        return of(result);
      }),
      mergeMap((data) => {
        // Remove Marker Targets
        const typedAnnotationTargets = store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
        const markerTargets = marker.targets.reduce((arr, targetId) => {
          const entity = structuredClone(typedAnnotationTargets[targetId]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.AnnotationTargetEntityModels.AnnotationTarget[])
        if (markerTargets.length > 0) {
          return forkJoin(markerTargets.map((markerTarget) => store.dispatch(new FlatActions.AnnotationTargetEntityActions.RemoveEntity(markerTarget))));
        }
        return of(data);
      }),
      mergeMap((data) => {
        // Get Annotation Label
        if (marker.label) {
          const typedAnnotationLabels = store.selectSnapshot(FlatStates.AnnotationLabelEntityState.typedEntities$);
          const annotationLabel = structuredClone(typedAnnotationLabels[marker.label]);
          return store.dispatch(new FlatActions.AnnotationLabelEntityActions.RemoveEntity(annotationLabel))
        }
        return of({data});
      }),
      mergeMap((data) => {
        // Get Annotation Source
        if (marker.source) {
          const typedAnnotationSources = store.selectSnapshot(FlatStates.AnnotationSourceEntityState.typedEntities$);
          const annotationSource = structuredClone(typedAnnotationSources[marker.source]);
          return store.dispatch(new FlatActions.AnnotationSourceEntityActions.RemoveEntity(annotationSource));
        }
        return of({data});
      }),
    );
  }
  
  //</editor-fold>
  
  //<editor-fold desc="Free Form">
  
  static addFreeForm(store: Store,
                     page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                     image: FlatModels.ImageEntityModels.Image | null,
                     poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                     shape: FlatModels.AnnotationEntityModels.Shape,
                     currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                     text: string | null,
                     clusterParentUpdate = false) {
    // Create New Free Form
    const newFreeForm: FlatModels.AnnotationEntityModels.FreeForm = {
      id: uuid(),
      type: FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm,
      shape: shape,
      path: [],
      fork: {
        x: null,
        y: null
      },
      selfTest: {
        ignore: false
      },
      images: image ? [image.id] : [],
      label: uuid(),
      source: uuid(),
      target: uuid(),
      freeFormStyle: uuid(),
      slide: page.id,
    };
    
    // Calculate Size And Position
    const scale = currentPositionAndZoom.isVM ? currentPositionAndZoom.currentPosition.zoom : 1;
    const normScale = currentPositionAndZoom.currentPosition.width / 800;
    const aspectRatio = currentPositionAndZoom.currentPosition.width / currentPositionAndZoom.currentPosition.height;
    const viewportWidth = 1 / currentPositionAndZoom.currentPosition.zoom;
    const freeFormWidth = viewportWidth * 0.1;
    const freeFormHeight = freeFormWidth * aspectRatio;
    const prevFontSize = 16 * normScale;
    const prevStrokeWidth = 2 * normScale;
    const fontSize = Math.ceil(prevFontSize / scale);
    const strokeWidth = Math.ceil(prevStrokeWidth / scale);
    const sourcePosition = {
      x: currentPositionAndZoom.currentPosition.x + freeFormWidth / 2,
      y: currentPositionAndZoom.currentPosition.y - freeFormHeight / 2
    };
    
    // Update Image
    if (image) {
      image.annotations.push(newFreeForm.id);
    }
    
    // Update Poi
    if (poi) {
      // Add to Poi
      poi.annotationToPointOfInterest.push({
        id: uuid(),
        annotation: newFreeForm.id,
        pointOfInterest: poi.id,
        order: poi.annotationToPointOfInterest?.length || 0
      });
    }
    
    // Update Page
    page.annotations.push(newFreeForm.id);
    
    // Create Free Form Pipe Object
    const freeFormPipeModel: FlatModels.PipeEntityModels.FreeFormPipeObject = {
      type: newFreeForm.type,
      data: {},
      annotation: newFreeForm,
    };
    
    // Add Free Form
    let layer: FlatModels.LayerEntityModels.Layer;
    return of(freeFormPipeModel).pipe(
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) =>
        store.dispatch(new FlatActions.AnnotationEntityActions.AddEntity(newFreeForm)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) =>
        FlatFacades.AnnotationLabelEntityFacade.addAnnotationLabel(store, newFreeForm.label, newFreeForm, text || 'Neue Freiform', currentPositionAndZoom.isVM ? fontSize : 12).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) =>
        FlatFacades.AnnotationSourceEntityFacade.addAnnotationSource(store, newFreeForm.source, newFreeForm, sourcePosition, currentPositionAndZoom.isVM ? currentPositionAndZoom.currentPosition.zoom : 1).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) => {
        const offset = currentPositionAndZoom.currentPosition.zoom ? { x: -0.025 / currentPositionAndZoom.currentPosition.zoom, y: 0.15 / currentPositionAndZoom.currentPosition.zoom } : { x: -0.025, y: 0.15 };
        const targetPosition = {
          x: sourcePosition.x + offset.x,
          y: sourcePosition.y + offset.y,
        }
        return FlatFacades.AnnotationTargetEntityFacade.addAnnotationTarget(store, newFreeForm.target!, newFreeForm, FlatModels.AnnotationEntityModels.TipType.enum.none, targetPosition).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data.data, target: data.annotationTarget}))),
        );
      }),
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) =>
        FlatFacades.FreeFormStyleEntityFacade.addFreeFormStyle(store, newFreeForm.freeFormStyle, newFreeForm, currentPositionAndZoom.isVM ? strokeWidth : 2, currentPositionAndZoom.isVM ? freeFormWidth : 0.1, currentPositionAndZoom.isVM ? freeFormHeight : 0.1).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) => {
        if (clusterParentUpdate || !image) {
          return of(result);
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) => {
        if (!poi) {
          return of(result);
        }
        return store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((result: FlatModels.PipeEntityModels.FreeFormPipeObject) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((data) => {
        if (image) {
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded(page.id));
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedLayerExpanded(image.layer));
          
          const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
          layer = typedLayers[image.layer];
          if (layer) {
            store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layer.position));
            
            const index = layer.images.indexOf(image.id);
            if (index > -1) {
              store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(index));
            }
          }
        } else {
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideAnnotationsExpanded(page.id));
        }
        store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(newFreeForm.id));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation));
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newFreeForm.id));
        store.dispatch(new FlatActions.SlideAuthorActions.ActivateDrawTool(newFreeForm));
        return of(data);
      }),
    );
  }
  
  static updateFreeFormPosition(store: Store,
                                annotation: FlatModels.AnnotationEntityModels.FreeForm,
                                currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Get Data
    const typedAnnotationSources = store.selectSnapshot(FlatStates.AnnotationSourceEntityState.typedEntities$);
    const typedAnnotationTargets = store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    let annotationSource = structuredClone(typedAnnotationSources[annotation.source]);
    let annotationTarget = null;
    if (annotation.target) {
      annotationTarget = structuredClone(typedAnnotationTargets[annotation.target]);
    }
    
    // Update Free Form Position
    const freeFormPositionHelper = new FreeFormPositionHelper(annotation, annotationSource, annotationTarget, currentPositionAndZoom);
    annotation = freeFormPositionHelper.annotation;
    annotationSource = freeFormPositionHelper.annotationSource;
    annotationTarget = freeFormPositionHelper.annotationTarget;
    
    // Update Free Form Position
    return store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(annotationSource)).pipe(
      mergeMap((data) => {
        if (annotationTarget) {
          return store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(annotationTarget));
        }
        return of(data);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation))
      ),
      mergeMap((data) => {
        store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(annotation.id));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation));
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(annotation.id));
        return of(data);
      }),
    );
  }
  
  static copyFreeForm(store: Store,
                      page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                      image: FlatModels.ImageEntityModels.Image | null,
                      newFreeForm: FlatModels.AnnotationEntityModels.FreeForm,
                      currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom | null = null,
                      clusterParentUpdate = false) {
    // Update Image
    if (image) {
      image.annotations.push(newFreeForm.id);
    }
    
    // Update Page
    page.annotations.push(newFreeForm.id);
    
    // Get Free Form Label
    const newFreeFormLabel = FlatFacades.AnnotationLabelEntityFacade.getCopyInstance(store, newFreeForm.label, newFreeForm);
    newFreeForm.label = newFreeFormLabel.id;
    
    // Get Free Form Source
    let newFreeFormSource = FlatFacades.AnnotationSourceEntityFacade.getCopyInstance(store, newFreeForm.source, newFreeForm);
    newFreeForm.source = newFreeFormSource.id;
    
    // Get Free Form Target
    let newFreeFormTarget = null;
    if (newFreeForm.target) {
      newFreeFormTarget = FlatFacades.AnnotationTargetEntityFacade.getCopyInstance(store, newFreeForm.target, newFreeForm);
      newFreeForm.target = newFreeFormTarget.id;
    }
    
    // Create New Free Form Style
    const newFreeFormStyle = FlatFacades.FreeFormStyleEntityFacade.getCopyInstance(store, newFreeForm.freeFormStyle, newFreeForm);
    newFreeForm.freeFormStyle = newFreeFormStyle.id;
    
    // Update Free Form Position
    if (currentPositionAndZoom) {
      const freeFormPositionHelper = new FreeFormPositionHelper(newFreeForm, newFreeFormSource, newFreeFormTarget, currentPositionAndZoom);
      newFreeForm = freeFormPositionHelper.annotation;
      newFreeFormSource = freeFormPositionHelper.annotationSource;
      newFreeFormTarget = freeFormPositionHelper.annotationTarget;
    }
    
    // Copy Free Form
    return store.dispatch(new FlatActions.AnnotationEntityActions.AddEntity(newFreeForm)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationLabelEntityActions.AddEntity(newFreeFormLabel))
      ),
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationSourceEntityActions.AddEntity(newFreeFormSource))
      ),
      mergeMap((data) => {
        if (newFreeFormTarget) {
          return store.dispatch(new FlatActions.AnnotationTargetEntityActions.AddEntity(newFreeFormTarget));
        }
        return of(data);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.FreeFormStyleEntityActions.AddEntity(newFreeFormStyle))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate || !image) {
          return of(data);
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image));
      }),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }
  
  static removeFreeForm(store: Store,
                        freeform: FlatModels.AnnotationEntityModels.FreeForm) {
    // Create Free Form Pipe Object
    const freeFormPipeModel: FlatModels.PipeEntityModels.FreeFormPipeObject = {
      type: freeform.type,
      data: {},
      annotation: freeform,
    };
    
    return of(freeFormPipeModel).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationEntityActions.RemoveEntity(freeform))
      ),
      mergeMap((data) => {
        // Get Annotation Label
        if (freeform.label) {
          const typedAnnotationLabels = store.selectSnapshot(FlatStates.AnnotationLabelEntityState.typedEntities$);
          const annotationLabel = structuredClone(typedAnnotationLabels[freeform.label]);
          return store.dispatch(new FlatActions.AnnotationLabelEntityActions.RemoveEntity(annotationLabel))
        }
        return of({data});
      }),
      mergeMap((data) => {
        // Get Annotation Source
        if (freeform.source) {
          const typedAnnotationSources = store.selectSnapshot(FlatStates.AnnotationSourceEntityState.typedEntities$);
          const annotationSource = structuredClone(typedAnnotationSources[freeform.source]);
          return store.dispatch(new FlatActions.AnnotationSourceEntityActions.RemoveEntity(annotationSource));
        }
        return of({data});
      }),
      mergeMap((data) => {
        // Get Annotation Target
        if (freeform.target) {
          const typedAnnotationTargets = store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
          const annotationTarget = structuredClone(typedAnnotationTargets[freeform.target]);
          return store.dispatch(new FlatActions.AnnotationTargetEntityActions.RemoveEntity(annotationTarget));
        }
        return of({data});
      }),
      mergeMap((data) => {
        // Get Free Form Style
        if (freeform.freeFormStyle) {
          const typedFreeFormStyles = store.selectSnapshot(FlatStates.FreeFormStyleEntityState.typedEntities$);
          const freeFormStyle = structuredClone(typedFreeFormStyles[freeform.freeFormStyle]);
          return store.dispatch(new FlatActions.FreeFormStyleEntityActions.RemoveEntity(freeFormStyle));
        }
        return of({data});
      }),
    );
  }
  
  //</editor-fold>
  
  //<editor-fold desc="Keymap">
  
  static addKeymap(store: Store,
                   page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                   image: FlatModels.ImageEntityModels.Image | null,
                   poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                   currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                   text: string | null,
                   clusterParentUpdate = false) {
    // Create New Keymap
    const newKeymap: FlatModels.AnnotationEntityModels.Keymap = {
      id: uuid(),
      type: FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap,
      fork: {
        x: null,
        y: null
      },
      selfTest: {
        ignore: false
      },
      images: image ? [image.id] : [],
      label: uuid(),
      source: uuid(),
      keymapStyle: uuid(),
      columns: [uuid()],
      slide: page.id,
    };
    
    // Calculate Size And Position
    const scale = currentPositionAndZoom.isVM ? currentPositionAndZoom.currentPosition.zoom : 1;
    const normScale = currentPositionAndZoom.currentPosition.width / 800;
    const aspectRatio = currentPositionAndZoom.currentPosition.width / currentPositionAndZoom.currentPosition.height;
    const viewportWidth = 1 / currentPositionAndZoom.currentPosition.zoom;
    const freeFormWidth = viewportWidth * 0.1;
    const freeFormHeight = freeFormWidth * aspectRatio;
    const prevFontSize = 16 * normScale;
    const fontSize = Math.ceil(prevFontSize / scale);
    const sourcePosition = {
      x: currentPositionAndZoom.currentPosition.x + freeFormWidth / 2,
      y: currentPositionAndZoom.currentPosition.y - freeFormHeight / 2
    };
    
    // Update Image
    if (image) {
      image.annotations.push(newKeymap.id);
    }
    
    // Update Poi
    if (poi) {
      // Add to Poi
      poi.annotationToPointOfInterest.push({
        id: uuid(),
        annotation: newKeymap.id,
        pointOfInterest: poi.id,
        order: poi.annotationToPointOfInterest?.length || 0
      });
    }
    
    // Update Page
    page.annotations.push(newKeymap.id);
    
    // Create Keymap Pipe Object
    const keymapPipeModel: FlatModels.PipeEntityModels.KeymapPipeObject = {
      type: newKeymap.type,
      data: {},
      annotation: newKeymap,
    };
    
    // Add Keymap
    let layer: FlatModels.LayerEntityModels.Layer;
    return of(keymapPipeModel).pipe(
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) =>
        store.dispatch(new FlatActions.AnnotationEntityActions.AddEntity(newKeymap)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) =>
        FlatFacades.AnnotationLabelEntityFacade.addAnnotationLabel(store, newKeymap.label, newKeymap, text || 'Neue Legende', currentPositionAndZoom.isVM ? fontSize : 12).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) =>
        FlatFacades.AnnotationSourceEntityFacade.addAnnotationSource(store, newKeymap.source, newKeymap, sourcePosition, currentPositionAndZoom.isVM ? currentPositionAndZoom.currentPosition.zoom : 1).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) =>
        FlatFacades.KeymapStyleEntityFacade.addKeymapStyle(store, newKeymap.keymapStyle, newKeymap).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) => {
        if (newKeymap.columns.length > 0) {
          return forkJoin(newKeymap.columns.map(id =>
            FlatFacades.KeymapColumnEntityFacade.addKeymapColumn(store, id, page, image, poi, newKeymap, currentPositionAndZoom)
          )).pipe(
            mergeMap((keymapColumnPipeObjects: FlatModels.PipeEntityModels.KeymapColumnPipeObject[]) => {
              for (const keymapColumnPipeObject of keymapColumnPipeObjects) {
                if (result.columns) {
                  result.columns.push(keymapColumnPipeObject);
                } else {
                  result.columns = [keymapColumnPipeObject];
                }
              }
              return of(result);
            }),
          );
        }
        return of(result);
      }),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) => {
        if (clusterParentUpdate || !image) {
          return of(result);
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) => {
        if (!poi) {
          return of(result);
        }
        return store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapPipeObject) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        );
      }),
      mergeMap((data) => {
        if (image) {
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded(page.id));
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedLayerExpanded(image.layer));
          
          const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
          layer = typedLayers[image.layer];
          if (layer) {
            store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layer.position));
            
            const index = layer.images.indexOf(image.id);
            if (index > -1) {
              store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(index));
            }
          }
        } else {
          store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideAnnotationsExpanded(page.id));
        }
        store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(newKeymap.id));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation));
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newKeymap.id));
        return of(data);
      }),
    );
  }
  
  static updateKeymapPosition(store: Store,
                              annotation: FlatModels.AnnotationEntityModels.Keymap,
                              currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Get Data
    const typedAnnotationSources = store.selectSnapshot(FlatStates.AnnotationSourceEntityState.typedEntities$);
    let annotationSource = structuredClone(typedAnnotationSources[annotation.source]);
    
    // Update Keymap Position
    const keymapPositionHelper = new KeymapPositionHelper(annotation, annotationSource, currentPositionAndZoom);
    annotation = keymapPositionHelper.annotation;
    annotationSource = keymapPositionHelper.annotationSource;
    
    // Update Keymap Position
    return store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(annotationSource)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation))
      ),
      mergeMap((data) => {
        store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(annotation.id));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation))
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(annotation.id));
        return of(data);
      }),
    );
  }
  
  static copyKeymap(store: Store,
                    mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                    page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                    image: FlatModels.ImageEntityModels.Image | null,
                    newKeymap: FlatModels.AnnotationEntityModels.Keymap,
                    currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom | null = null,
                    clusterParentUpdate = false) {
    // Update Page
    page.annotations.push(newKeymap.id);
    
    // Update Image
    if (image) {
      image.annotations.push(newKeymap.id);
    }
    
    // Get Keymap Label
    const newKeymapLabel = FlatFacades.AnnotationLabelEntityFacade.getCopyInstance(store, newKeymap.label, newKeymap);
    newKeymap.label = newKeymapLabel.id;
    
    // Get Keymap Source
    let newKeymapSource = FlatFacades.AnnotationSourceEntityFacade.getCopyInstance(store, newKeymap.source, newKeymap);
    newKeymap.source = newKeymapSource.id;
    
    // Get Keymap Style
    const newKeymapStyle = FlatFacades.KeymapStyleEntityFacade.getCopyInstance(store, newKeymap.keymapStyle, newKeymap);
    newKeymap.keymapStyle = newKeymapStyle.id;
    
    // Column Fix
    const columns = structuredClone(newKeymap.columns);
    newKeymap.columns = [];
    const updateKeymap = structuredClone(newKeymap);
    
    // Get Keymap Columns, Labels and Annotations
    const newKeymapColumns: FlatModels.KeymapColumnEntityModels.KeymapColumn[] = [];
    const newKeymapLabels: FlatModels.KeymapLabelEntityModels.KeymapLabel[] = [];
    for (const column of columns) {
      const newKeymapColumn = FlatFacades.KeymapColumnEntityFacade.getCopyInstance(store, column, newKeymap);
      
      // Label Fix
      const labels = structuredClone(newKeymapColumn.labels);
      newKeymapColumn.labels = [];
      
      // Add To Arrays
      newKeymapColumns.push(newKeymapColumn);
      updateKeymap.columns.push(newKeymapColumn.id);
      
      // Get Keymap Labels
      for (const label of labels) {
        const newKeymapLabel = FlatFacades.KeymapLabelEntityFacade.getCopyInstance(store, label, newKeymapColumn);
        newKeymapLabels.push(newKeymapLabel);
        newKeymapColumn.labels.push(newKeymapLabel.id);
      }
    }
    
    // Update Keymap Position
    if (currentPositionAndZoom) {
      const keymapPositionHelper = new KeymapPositionHelper(newKeymap, newKeymapSource, currentPositionAndZoom);
      newKeymap = keymapPositionHelper.annotation;
      newKeymapSource = keymapPositionHelper.annotationSource;
    }
    
    // Copy Marker
    return store.dispatch(new FlatActions.AnnotationEntityActions.AddEntity(newKeymap)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationLabelEntityActions.AddEntity(newKeymapLabel))
      ),
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationSourceEntityActions.AddEntity(newKeymapSource))
      ),
      mergeMap(() =>
        store.dispatch(new FlatActions.KeymapStyleEntityActions.AddEntity(newKeymapStyle))
      ),
      mergeMap((data): any => {
        if (newKeymapLabels.length > 0) {
          return forkJoin(newKeymapLabels.map(newKeymapLabel => {
            if (newKeymapLabel.annotation) {
              if (!mappings.annotation[newKeymapLabel.annotation]) {
                // Get Data
                const typedAnnotations = store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
                
                // Get Annotation
                const annotation = structuredClone(typedAnnotations[newKeymapLabel.annotation]);
                
                // Update Annotation Mapping
                mappings.annotation[annotation.id] = uuid();
                
                // Update Annotation
                annotation.id = mappings.annotation[annotation.id];
                annotation.slide = page.id;
                
                // Copy Annotation By Type
                switch (annotation.type) {
                  case FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker:
                  case FlatModels.AnnotationEntityModels.AnnotationType.enum.Text:
                    return AnnotationEntityFacade.copyMarker(store, page, image, annotation as FlatModels.AnnotationEntityModels.Marker, currentPositionAndZoom, clusterParentUpdate).pipe(
                      mergeMap(() => {
                        newKeymapLabel.annotation = annotation.id;
                        return store.dispatch(new FlatActions.KeymapLabelEntityActions.AddEntity(newKeymapLabel));
                      }),
                    );
                  case FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm:
                    return AnnotationEntityFacade.copyFreeForm(store, page, image, annotation as FlatModels.AnnotationEntityModels.FreeForm, currentPositionAndZoom, clusterParentUpdate).pipe(
                      mergeMap(() => {
                        newKeymapLabel.annotation = annotation.id;
                        return store.dispatch(new FlatActions.KeymapLabelEntityActions.AddEntity(newKeymapLabel));
                      }),
                    );
                  case FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap:
                    return AnnotationEntityFacade.copyKeymap(store, mappings, page, image, annotation as FlatModels.AnnotationEntityModels.Keymap, currentPositionAndZoom, clusterParentUpdate).pipe(
                      mergeMap(() => {
                        newKeymapLabel.annotation = annotation.id;
                        return store.dispatch(new FlatActions.KeymapLabelEntityActions.AddEntity(newKeymapLabel));
                      }),
                    );
                  default:
                    throw new FlatModels.ErrorControlModels.MedsurfError(
                      'notification.type_not_supported',
                      FlatModels.ErrorControlModels.ErrorLevel.enum.error,
                      FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
                      "LIBS.FlatFacades.AnnotationEntityFacade.copyKeymap"
                    );
                }
              }
              newKeymapLabel.annotation = mappings.annotation[newKeymapLabel.annotation];
            }
            return store.dispatch(new FlatActions.KeymapLabelEntityActions.AddEntity(newKeymapLabel));
          }));
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (newKeymapColumns.length > 0) {
          return forkJoin(newKeymapColumns.map(newKeymapColumn => store.dispatch(new FlatActions.KeymapColumnEntityActions.AddEntity(newKeymapColumn))));
        }
        return of(data);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(updateKeymap))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate || !image) {
          return of(data);
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image));
      }),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }
  
  static removeKeymap(store: Store,
                      keymap: FlatModels.AnnotationEntityModels.Keymap) {
    // Create Keymap Pipe Object
    const keymapPipeModel: FlatModels.PipeEntityModels.KeymapPipeObject = {
      type: keymap.type,
      data: {},
      annotation: keymap,
    };
    
    return of(keymapPipeModel).pipe(
      mergeMap((data) => {
        // Remove Keymap Columns
        const typedKeymapColumns = store.selectSnapshot(FlatStates.KeymapColumnEntityState.typedEntities$);
        const keymapColumns = keymap.columns.reduce((arr, columnId) => {
          const entity = structuredClone(typedKeymapColumns[columnId]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.KeymapColumnEntityModels.KeymapColumn[])
        // Remove Keymap Labels
        const typedKeymapLabels = store.selectSnapshot(FlatStates.KeymapLabelEntityState.typedEntities$);
        const keymapLabels = keymapColumns.reduce((arr, keymapColumn) => {
          for (const label of keymapColumn.labels) {
            const entity = structuredClone(typedKeymapLabels[label]);
            if (entity) {
              arr.push(entity);
            }
          }
          return arr;
        }, [] as FlatModels.KeymapLabelEntityModels.KeymapLabel[]);
        if (keymapLabels.length > 0) {
          return forkJoin(keymapLabels.map((keymapLabel) => store.dispatch(new FlatActions.KeymapLabelEntityActions.RemoveEntity(keymapLabel))))
        }
        return of(data);
      }),
      mergeMap((data) => {
        // Remove Keymap Columns
        const typedKeymapColumns = store.selectSnapshot(FlatStates.KeymapColumnEntityState.typedEntities$);
        const keymapColumns = keymap.columns.reduce((arr, columnId) => {
          const entity = structuredClone(typedKeymapColumns[columnId]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.KeymapColumnEntityModels.KeymapColumn[])
        if (keymapColumns.length > 0) {
          return forkJoin(keymapColumns.map((keymapColumn) => store.dispatch(new FlatActions.KeymapColumnEntityActions.RemoveEntity(keymapColumn))))
        }
        return of(data);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationEntityActions.RemoveEntity(keymap))
      ),
      mergeMap((data) => {
        // Get Annotation Label
        if (keymap.label) {
          const typedAnnotationLabels = store.selectSnapshot(FlatStates.AnnotationLabelEntityState.typedEntities$);
          const annotationLabel = structuredClone(typedAnnotationLabels[keymap.label]);
          return store.dispatch(new FlatActions.AnnotationLabelEntityActions.RemoveEntity(annotationLabel))
        }
        return of({data});
      }),
      mergeMap((data) => {
        // Get Annotation Source
        if (keymap.source) {
          const typedAnnotationSources = store.selectSnapshot(FlatStates.AnnotationSourceEntityState.typedEntities$);
          const annotationSource = structuredClone(typedAnnotationSources[keymap.source]);
          return store.dispatch(new FlatActions.AnnotationSourceEntityActions.RemoveEntity(annotationSource));
        }
        return of({data});
      }),
    );
  }
  
  //</editor-fold>

  //</editor-fold>
}

export class MarkerPositionHelper {
  constructor(public annotation: FlatModels.AnnotationEntityModels.Marker | FlatModels.AnnotationEntityModels.Text,
              public annotationSource: FlatModels.AnnotationSourceEntityModels.AnnotationSource | null,
              public annotationTargets: FlatModels.AnnotationTargetEntityModels.AnnotationTarget[],
              public currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Calculate Size And Position
    const aspectRatio = currentPositionAndZoom.currentPosition.width / currentPositionAndZoom.currentPosition.height;
    const viewportWidth = 1 / currentPositionAndZoom.currentPosition.zoom;
    const freeFormWidth = viewportWidth * 0.1;
    const freeFormHeight = freeFormWidth * aspectRatio;
    const sourcePosition = {
      x: currentPositionAndZoom.currentPosition.x + freeFormWidth / 2,
      y: currentPositionAndZoom.currentPosition.y - freeFormHeight / 2
    };
    
    // Update Marker Position
    let oldPosition: FlatModels.PointSharedModels.Point = {x: 0, y: 0};
    if (this.annotationSource) {
      oldPosition = structuredClone(this.annotationSource.position);
      this.annotationSource.position = sourcePosition;
    }
    
    // Update Marker Fork
    if (this.annotation.fork && this.annotation.fork.x && this.annotation.fork.y) {
      this.annotation.fork.x = this.annotation.fork.x - (oldPosition.x || 0) + (sourcePosition.x || 0);
      this.annotation.fork.y = this.annotation.fork.y - (oldPosition.y || 0) + (sourcePosition.y || 0);
    }
    
    // Update Marker Target
    this.annotationTargets.forEach((annotationTarget) => {
      annotationTarget.position.x = (annotationTarget.position.x || 0) - (oldPosition?.x || 0) + (sourcePosition.x || 0);
      annotationTarget.position.y = (annotationTarget.position.y || 0) - (oldPosition?.y || 0) + (sourcePosition.y || 0);
    });
  }
}

export class FreeFormPositionHelper {
  constructor(public annotation: FlatModels.AnnotationEntityModels.FreeForm,
              public annotationSource: FlatModels.AnnotationSourceEntityModels.AnnotationSource,
              public annotationTarget: FlatModels.AnnotationTargetEntityModels.AnnotationTarget | null,
              public currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Calculate Size And Position
    const aspectRatio = currentPositionAndZoom.currentPosition.width / currentPositionAndZoom.currentPosition.height;
    const viewportWidth = 1 / currentPositionAndZoom.currentPosition.zoom;
    const freeFormWidth = viewportWidth * 0.1;
    const freeFormHeight = freeFormWidth * aspectRatio;
    const sourcePosition = {
      x: currentPositionAndZoom.currentPosition.x + freeFormWidth / 2,
      y: currentPositionAndZoom.currentPosition.y - freeFormHeight / 2
    };
    
    // Update Free Form Position
    const oldPosition = structuredClone(this.annotationSource.position);
    this.annotationSource.position = sourcePosition;
    
    // Update Free Form Fork
    if (this.annotation.fork && this.annotation.fork.x && this.annotation.fork.y) {
      this.annotation.fork.x = this.annotation.fork.x - (oldPosition?.x || 0) + (sourcePosition.x || 0);
      this.annotation.fork.y = this.annotation.fork.y - (oldPosition?.y || 0) + (sourcePosition.y || 0);
    }
    
    // Update Free Form Path
    if ((this.annotation.shape === FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea || this.annotation.shape === FlatModels.AnnotationEntityModels.Shape.enum.free) && this.annotation.path) {
      this.annotation.path.forEach((entry) => {
        entry.x = (entry.x || 0) - (oldPosition?.x || 0) + (sourcePosition.x || 0);
        entry.y = (entry.y || 0) - (oldPosition?.y || 0) + (sourcePosition.y || 0);
      });
    }
    
    // Update Free Form Target
    if (annotationTarget) {
      annotationTarget.position.x = (annotationTarget.position.x || 0) - (oldPosition?.x || 0) + (sourcePosition.x || 0);
      annotationTarget.position.y = (annotationTarget.position.y || 0) - (oldPosition?.y || 0) + (sourcePosition.y || 0);
    }
  }
}

export class KeymapPositionHelper {
  constructor(public annotation: FlatModels.AnnotationEntityModels.Keymap,
              public annotationSource: FlatModels.AnnotationSourceEntityModels.AnnotationSource,
              public currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Calculate Size And Position
    const aspectRatio = currentPositionAndZoom.currentPosition.width / currentPositionAndZoom.currentPosition.height;
    const viewportWidth = 1 / currentPositionAndZoom.currentPosition.zoom;
    const freeFormWidth = viewportWidth * 0.1;
    const freeFormHeight = freeFormWidth * aspectRatio;
    const sourcePosition = {
      x: currentPositionAndZoom.currentPosition.x + freeFormWidth / 2,
      y: currentPositionAndZoom.currentPosition.y - freeFormHeight / 2
    };
    
    // Update Keymap Position
    const oldPosition = structuredClone(this.annotationSource.position);
    this.annotationSource.position = sourcePosition;
    
    // Update Keymap Fork
    if (this.annotation.fork && this.annotation.fork.x && this.annotation.fork.y) {
      this.annotation.fork.x = this.annotation.fork.x - (oldPosition?.x || 0) + (sourcePosition.x || 0);
      this.annotation.fork.y = this.annotation.fork.y - (oldPosition?.y || 0) + (sourcePosition.y || 0);
    }
    
    // Update Keymap Selftest
    if (this.annotation.selfTest && this.annotation.selfTest.x && this.annotation.selfTest.y) {
      this.annotation.selfTest.x = this.annotation.selfTest.x - (oldPosition?.x || 0) + (sourcePosition.x || 0);
      this.annotation.selfTest.y = this.annotation.selfTest.y - (oldPosition?.y || 0) + (sourcePosition.y || 0);
    }
  }
}


